import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckCircle2, ChevronRight, Sparkles, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../contexts/authContext';
import { useComplianceMatrix } from '../../../../hooks/useComplianceMatrix';
import { ComplianceStatus } from '../../../../types/proposal';
import { greeting } from '../../../../utils/time';
import { DeadlineCountdown } from './DeadlineCountdown';
/**
 * Left half of the V2 Home page. Workflow lens — surfaces what *this user*
 * needs to do next, with the deadline always in sight.
 *
 * Sections (top to bottom):
 *   1. Greeting + deadline chip
 *   2. My assigned requirements (filters compliance matrix by current user)
 *   3. Recent changes (last 8 from the unified activity feed)
 *   4. Next 3 actions — hard-coded placeholder for V1; LLM gen in a follow-up
 */ export function MyWorkPanel({ projectId, dueDate }) {
    const { user } = useAuth();
    const matrix = useComplianceMatrix(projectId);
    const matrixQuery = matrix;
    const myRequirements = (matrix.data?.requirements ?? []).filter((r)=>r.assignedToUserId && user && r.assignedToUserId === user.id).slice(0, 6);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full flex-col gap-4 overflow-auto p-4 sm:p-6 lg:p-8",
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex flex-wrap items-center justify-between gap-3",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs font-medium uppercase tracking-wide text-slate-500",
                                children: "My Work"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mt-0.5 text-2xl font-semibold text-slate-900",
                                children: greeting(user?.name)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(DeadlineCountdown, {
                        dueDate: dueDate,
                        variant: "chip"
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Section, {
                title: "Assigned to you",
                icon: /*#__PURE__*/ _jsx(Target, {
                    className: "h-4 w-4 text-slate-500",
                    "aria-hidden": "true"
                }),
                action: myRequirements.length > 0 ? /*#__PURE__*/ _jsx(Link, {
                    to: `/proposal/${projectId}/v2/compliance`,
                    className: "text-xs font-medium text-blue-600 hover:text-blue-700",
                    children: "View all"
                }) : null,
                children: matrix.isLoading ? /*#__PURE__*/ _jsx(SkeletonRows, {
                    count: 3
                }) : myRequirements.length === 0 ? /*#__PURE__*/ _jsx(EmptyState, {
                    icon: /*#__PURE__*/ _jsx(CheckCircle2, {
                        className: "h-5 w-5 text-emerald-500",
                        "aria-hidden": "true"
                    }),
                    title: "Nothing on your plate",
                    body: "Once teammates assign requirements to you, they'll show up here."
                }) : /*#__PURE__*/ _jsx("ul", {
                    className: "space-y-2",
                    children: myRequirements.map((req)=>/*#__PURE__*/ _jsx(RequirementRow, {
                            req: req,
                            projectId: projectId
                        }, req.requirementId))
                })
            }),
            /*#__PURE__*/ _jsx(Section, {
                title: "Compliance snapshot",
                icon: /*#__PURE__*/ _jsx(Target, {
                    className: "h-4 w-4 text-slate-500",
                    "aria-hidden": "true"
                }),
                children: matrixQuery.isLoading ? /*#__PURE__*/ _jsx(SkeletonRows, {
                    count: 3
                }) : !matrixQuery.data?.statistics ? /*#__PURE__*/ _jsx(EmptyState, {
                    icon: /*#__PURE__*/ _jsx(Target, {
                        className: "h-5 w-5 text-slate-400",
                        "aria-hidden": "true"
                    }),
                    title: "No requirements yet",
                    body: "Process the solicitation to populate the compliance matrix."
                }) : /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-slate-600",
                                    children: "Total requirements"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-slate-900",
                                    children: matrixQuery.data.statistics.total
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-slate-600",
                                    children: "Addressed"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-emerald-600",
                                    children: matrixQuery.data.statistics.addressed
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-slate-600",
                                    children: "Not started"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-slate-500",
                                    children: matrixQuery.data.statistics.notAddressed
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Link, {
                            to: `/proposal/${projectId}/v2/compliance`,
                            className: "mt-1 flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800",
                            children: [
                                "Open compliance matrix",
                                /*#__PURE__*/ _jsx(ChevronRight, {
                                    className: "h-3 w-3"
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(Section, {
                title: "Next best actions",
                icon: /*#__PURE__*/ _jsx(Sparkles, {
                    className: "h-4 w-4 text-violet-500",
                    "aria-hidden": "true"
                }),
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col items-center gap-2 rounded-lg border border-dashed border-violet-200 bg-violet-50/30 px-4 py-6 text-center",
                    children: [
                        /*#__PURE__*/ _jsx(Sparkles, {
                            className: "h-5 w-5 text-violet-400",
                            "aria-hidden": "true"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-sm font-medium text-slate-700",
                            children: "AI-powered suggestions"
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "text-xs text-slate-500",
                            children: "Personalized next actions will appear here based on your role and proposal progress."
                        })
                    ]
                })
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Sub-components (kept local — only used by MyWorkPanel)
// ---------------------------------------------------------------------------
function Section({ title, icon, action, children }) {
    return /*#__PURE__*/ _jsxs("section", {
        className: "rounded-xl border border-slate-200 bg-white p-4 shadow-sm",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 flex items-center justify-between",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2 text-sm font-semibold text-slate-900",
                        children: [
                            icon,
                            /*#__PURE__*/ _jsx("span", {
                                children: title
                            })
                        ]
                    }),
                    action
                ]
            }),
            children
        ]
    });
}
function RequirementRow({ req, projectId }) {
    return /*#__PURE__*/ _jsx("li", {
        children: /*#__PURE__*/ _jsxs(Link, {
            to: `/proposal/${projectId}/v2/compliance`,
            className: "group flex items-start justify-between gap-3 rounded-lg border border-slate-100 bg-white px-3 py-2 text-sm hover:border-slate-300 hover:bg-slate-50",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "min-w-0 flex-1",
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "line-clamp-2 text-slate-800",
                            children: req.source?.exactText || 'Untitled requirement'
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-1 flex items-center gap-2 text-[11px] text-slate-500",
                            children: [
                                /*#__PURE__*/ _jsx(StatusPill, {
                                    status: req.status
                                }),
                                req.source?.filename ? /*#__PURE__*/ _jsx("span", {
                                    className: "truncate",
                                    children: req.source.filename
                                }) : null
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(ChevronRight, {
                    className: "mt-1 h-4 w-4 flex-shrink-0 text-slate-300 group-hover:text-slate-500",
                    "aria-hidden": "true"
                })
            ]
        })
    });
}
function StatusPill({ status }) {
    const map = {
        [ComplianceStatus.NOT_STARTED]: 'border-slate-200 bg-slate-50 text-slate-600',
        [ComplianceStatus.IN_PROGRESS]: 'border-blue-200 bg-blue-50 text-blue-700',
        [ComplianceStatus.ADDRESSED]: 'border-emerald-200 bg-emerald-50 text-emerald-700',
        [ComplianceStatus.PARTIAL]: 'border-amber-200 bg-amber-50 text-amber-700',
        [ComplianceStatus.NON_COMPLIANT]: 'border-rose-200 bg-rose-50 text-rose-700',
        [ComplianceStatus.NOT_APPLICABLE]: 'border-slate-200 bg-slate-50 text-slate-500'
    };
    return /*#__PURE__*/ _jsx("span", {
        className: `inline-flex items-center rounded-full border px-1.5 py-px ${map[status] ?? map[ComplianceStatus.NOT_STARTED]}`,
        children: humanizeStatus(status)
    });
}
function EmptyState({ icon, title, body }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex items-start gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/40 px-4 py-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "mt-0.5",
                children: icon
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "text-sm font-medium text-slate-800",
                        children: title
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-0.5 text-xs text-slate-600",
                        children: body
                    })
                ]
            })
        ]
    });
}
function SkeletonRows({ count }) {
    return /*#__PURE__*/ _jsx("ul", {
        className: "space-y-2",
        children: Array.from({
            length: count
        }).map((_, i)=>/*#__PURE__*/ _jsx("li", {
                className: "h-12 animate-shimmer rounded-lg bg-slate-100"
            }, i))
    });
}
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function humanizeStatus(s) {
    switch(s){
        case ComplianceStatus.NOT_STARTED:
            return 'Not started';
        case ComplianceStatus.IN_PROGRESS:
            return 'In progress';
        case ComplianceStatus.ADDRESSED:
            return 'Addressed';
        case ComplianceStatus.PARTIAL:
            return 'Partial';
        case ComplianceStatus.NON_COMPLIANT:
            return 'Non-compliant';
        case ComplianceStatus.NOT_APPLICABLE:
            return 'N/A';
        default:
            return s;
    }
}
